<template>
    <div>
        <div class="row b-b">
            <div class="col-12 p-3">
                <company-form-token></company-form-token>
            </div>
        </div>
        <div class="row">
            <div class="col-12 p-3">
                <info-alert-box alert-type="warning"
                                text="Please note that the AloAi Outbound Call API usage is considered automated & paid. You are agreeing to our billing terms by using the API."
                                kb-link="https://support.aloware.com/en/articles/9033804-aloware-pricing-plans-empowering-your-teams-with-flexible-solutions">
                </info-alert-box>

                <div class="mb-2">
                    <strong class="integration-api-titles">Make an AloAi outbound call to a contact.</strong>
                </div>

                <p class="text-md">
                    <span class="text-dark-greenish _600">POST /api/v1/webhook/aloai-outbound-call</span>
                </p>

                <div class="mb-2">
                    <strong>Required Fields:</strong>
                    <br>
                    <div class="text-dark-greenish _600">api_token</div>
                    <div class="text-dark-greenish _600">bot_id</div>
                    <div class="text-dark-greenish _600">to <span class="text-black-lt">or</span> contact_phone_number <span class="text-black-lt">or</span> contact_id</div>
                </div>

                <div class="mb-2">
                    <strong>Optional Fields:</strong>
                    <br>
                    <div class="text-dark-greenish _600">from <span class="text-black-lt">or</span> line_phone_number <span class="text-black-lt">or</span> line_id (if not provided, uses bot's default outbound line)</div>
                </div>

                <p class="mb-2">
                    <strong>Field Aliases (SMS API compatible):</strong>
                    <br>
                    <span class="text-muted">
                        <span class="text-dark-greenish _600">from</span> = <span class="text-dark-greenish _600">line_phone_number</span> (line's phone number in E164 format)
                        <br>
                        <span class="text-dark-greenish _600">to</span> = <span class="text-dark-greenish _600">contact_phone_number</span> (contact's phone number in E164 format)
                    </span>
                </p>

                <p>
                    <strong>Example POST URL:</strong>
                    <br>
                    <span class="text-dark-greenish _600">{{ statics.domain | fixDomain }}/api/v1/webhook/aloai-outbound-call</span>
                </p>

                <p class="mb-2">
                    <strong>Example POST body:</strong>
                    <br>
                    <span class="text-muted">
                        Using from/to (SMS API style) - make an AloAi outbound call with phone numbers:
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_aloai_a'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "bot_id": 1, // put your AloAi bot's id here
    "from": "+18552562001", // put one of your line's phone numbers here
    "to": "+18181234567" // put your lead's phone number here
}</code></pre>

                <p class="mb-2">
                    <strong>Example POST body:</strong>
                    <br>
                    <span class="text-muted">
                        Using line_id and contact_phone_number - make an AloAi outbound call with line ID:
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_aloai_b'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "bot_id": 1, // put your AloAi bot's id here
    "line_id": 1234, // Aloware Line ID to call from
    "contact_phone_number": "+18181234567" // put your lead's phone number here
}</code></pre>

                <p class="mb-2">
                    <strong>Example POST body:</strong>
                    <br>
                    <span class="text-muted">
                        Using contact_id - make an AloAi outbound call to an existing contact:
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_aloai_c'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "bot_id": 1, // put your AloAi bot's id here
    "from": "+18552562001", // put one of your line's phone numbers here
    "contact_id": 456 // put your contact's id here
}</code></pre>

                <p class="mb-2">
                    <strong>Example POST body:</strong>
                    <br>
                    <span class="text-muted">
                        Without line (uses bot's default outbound line):
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_aloai_d'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "bot_id": 1, // put your AloAi bot's id here
    "to": "+18181234567" // put your lead's phone number here
}</code></pre>

                <p class="mb-2">
                    <strong>Example POST header:</strong>
                </p>
                <pre v-highlightjs><code class="json">{
    "Accept": "application/json",
    "Content-Type": "application/json"
}</code></pre>

                <p class="mb-2">
                    <strong>Returns an HTTP 202 response when the AloAi outbound call is initiated, Example success response:</strong>
                </p>
                <pre v-highlightjs><code class="json">{
    "message": "AloAi outbound call initiated."
}</code></pre>

                <p class="mb-2">
                    <strong>Returns an HTTP 422 on failure with a detailed response, Example failure response:</strong>
                </p>
                <pre v-highlightjs><code class="json">{
    "message": "The given data was invalid.",
    "errors": {
        "bot_id": [
            "The bot_id must be a valid outbound voice AloAi bot belonging to your company."
        ],
        "to": [
            "The to field is required when contact_phone_number and contact_id are empty."
        ]
    }
}</code></pre>

                <p class="mb-2">
                    <strong>cURL Example:</strong>
                    <span class="text-muted">
                        With from/to (SMS API style).
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_aloai_f'" v-if="!loading_whitelabel"><code class="bash">curl -X POST \
  {{ statics.domain | fixDomain }}/api/v1/webhook/aloai-outbound-call \
  -H 'Accept: application/json' \
  -H 'Content-Type: application/json' \
  -d '{
    "api_token": "{{ companyToken }}",
    "bot_id": 1,
    "from": "+18552562001",
    "to": "+18181234567"
  }'</code></pre>

                <p class="mb-2">
                    <strong>cURL Example:</strong>
                    <span class="text-muted">
                        With line_id and contact_id.
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_aloai_g'" v-if="!loading_whitelabel"><code class="bash">curl -X POST \
  {{ statics.domain | fixDomain }}/api/v1/webhook/aloai-outbound-call \
  -H 'Accept: application/json' \
  -H 'Content-Type: application/json' \
  -d '{
    "api_token": "{{ companyToken }}",
    "bot_id": 1,
    "line_id": 1234,
    "contact_id": 456
  }'</code></pre>

                <p class="_600"
                   v-if="!statics.whitelabel">
                    If you need more API functions, please contact our support at support@aloware.com .
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { company_token_mixin } from '../../mixins';
import CompanyFormToken from '../account/company-form-token';
import InfoAlertBox from "../info-alert-box.vue";

export default {
    name: "integrations-aloai-outbound-call-api",

    mixins: [
        company_token_mixin
    ],

    components: {
        InfoAlertBox,
        CompanyFormToken
    },

    data() {
        return {
            statics: {
                domain: null,
                whitelabel: false,
            },
            loading_whitelabel: true
        }
    },

    created() {
        this.getStatics()
    },

    methods: {
        getStatics() {
            this.loading_whitelabel = true
            axios.get('/get-statics')
                .then(res => {
                    this.statics = res.data
                    this.loading_whitelabel = false
                })
                .catch(err => {
                    console.log(err)
                    this.$root.handleErrors(err.response)
                    this.loading_whitelabel = false
                })
        }
    }
}
</script>
